import { useEffect, useMemo } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'

import { orderBy } from 'lodash'
import { FormApi } from 'final-form'
import { FIND_ONE_AI_TEST, UPDATE_AI_TEST, UPDATE_AI_TEST_QUESTION } from 'queries/responseTests'
import { PageLoader } from 'components/PageLoader'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { toast } from 'utils/toast'
import { UpdateAiTestQuestion, UpdateAiTestQuestionVariables } from '__generatedTypes__/UpdateAiTestQuestion'
import { MembershipType, UpdateAiTestInput, UpdateAiTestQuestionInput } from '__generatedTypes__/globalTypes'
import { QUERY_PARAM } from 'pagesNew/Users'
import { findOneAiTest, findOneAiTestVariables } from '__generatedTypes__/findOneAiTest'
import { updateAiTest, updateAiTestVariables } from '__generatedTypes__/updateAiTest'
import { Form, FormValues } from './Form'

export const EditResponseTest = () => {
  const { testId = '' } = useParams()

  const { navigate } = useLink()

  const { data, loading } = useQuery<findOneAiTest, findOneAiTestVariables>(FIND_ONE_AI_TEST, {
    variables: {
      id: testId,
    },
    skip: !testId,
    onCompleted: (data) => {
      if (!data.findOneAiTest) {
        goToTestsList()
      }
    },
    fetchPolicy: 'cache-and-network',
  })
  const test = data?.findOneAiTest
  const isFltTest = !!test?.casperFullLengthTest?.id

  const [updateTest] = useMutation<updateAiTest, updateAiTestVariables>(UPDATE_AI_TEST)
  const [updateTestQuestion] = useMutation<UpdateAiTestQuestion, UpdateAiTestQuestionVariables>(UPDATE_AI_TEST_QUESTION)

  useEffect(() => {
    if (!testId) {
      goToTestsList()
    }
  }, [testId])

  const goToTestsList = () => {
    if (isFltTest) {
      navigate(cmsRoutes.RESPONSE_FULL_LENGTH_TEST, undefined, { replace: true })
      return
    }

    const searchParams = new URLSearchParams({ [QUERY_PARAM]: test?.type || '' })
    navigate(cmsRoutes.RESPONSE_TESTS, undefined, { searchParams: searchParams, replace: true })
  }

  const sortedQuestions = useMemo(() => orderBy(test?.questions, 'order') || [], [test?.questions])

  const handleSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
    try {
      const { dirtyFields } = form.getState()
      const testFieldsChanged =
        dirtyFields.title ||
        dirtyFields.description ||
        dirtyFields.contextText ||
        dirtyFields.contextVideoUrl ||
        dirtyFields.videoText ||
        dirtyFields.membershipLevel ||
        dirtyFields.isLive1 ||
        dirtyFields.isLive2 ||
        dirtyFields.isLive3

      if (testFieldsChanged) {
        const updateData: UpdateAiTestInput = {
          id: values.id,
          title: values.title,
          description: values.description,
          membershipLevel: values.membershipLevel ?? MembershipType.Free,
          contextText: values.contextText,
          contextVideoUrl: values.contextVideoUrl,
          videoText: values.videoText,
          isLive: values.isLive1 || values.isLive2 || values.isLive3 ? values.isLive : false,
        }
        await updateTest({ variables: { input: updateData } })
      }

      const questionPromises =
        sortedQuestions.map((question, index) => {
          const changed =
            dirtyFields[`textBestResponse${index + 1}` as keyof FormValues] ||
            dirtyFields[`videoBestResponse${index + 1}` as keyof FormValues] ||
            dirtyFields[`isLive${index + 1}` as keyof FormValues]

          if (changed) {
            const updateData: UpdateAiTestQuestionInput = {
              id: question.id,
              bestResponseText: (values[`textBestResponse${index + 1}` as keyof FormValues] || null) as string | null,
              bestResponseVideoUrl: (values[`videoBestResponse${index + 1}` as keyof FormValues] || null) as
                | string
                | null,
              isLive: Boolean(values[`isLive${index + 1}` as keyof FormValues]),
            }

            return updateTestQuestion({ variables: { input: updateData } })
          }
          return Promise.resolve()
        }) || []

      await Promise.all(questionPromises)

      toast.success('Response Test saved successfully')
      goToTestsList()
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        toast.error(String(error.message))
      } else {
        toast.error('Error while saving test')
      }
    }
  }

  if (loading || !test) {
    return <PageLoader />
  }

  return <Form onSubmit={handleSubmit} responseTest={{ ...test, questions: sortedQuestions }} isFltTest={isFltTest} />
}
